import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IItemBase, IReportTemplate, IUser } from '@shared/interfaces';
import { PageEvent } from '@angular/material/paginator';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ReportTemplateService } from '@shared/services/report-template.service';
import { IPaginatedResult } from '@shared/interfaces/mailing/IPaginatedResult';
import { debounceTime, distinctUntilChanged, filter, firstValueFrom, Subject, takeUntil, tap } from 'rxjs';
import { IQueryReportTemplate } from '@shared/interfaces/queries/IQueryReportTemplate';
import { TemplateEditorPreviewComponent } from '@app/main/template-editor';
import { disableBackgroundScrolling, enableBackgroundScrolling } from '@shared/helpers/helpers';

@Component({
    selector: 'reporting-tool-template-selection',
    templateUrl: './reporting-tool-template-selection.component.html',
    styleUrls: ['./reporting-tool-template-selection.component.scss'],
    standalone: false
})
export class ReportingToolTemplateSelectionComponent implements OnInit, OnDestroy {
    @ViewChild('itemContainer') itemContainer: ElementRef;

    protected pagedData: IPaginatedResult<IReportTemplate>;
    protected value: IReportTemplate;
    protected keyUp = new Subject<KeyboardEvent>();
    protected query: { title: string } = { title: '' };

    private event: PageEvent;
    private loading: boolean;
    private destroy$ = new Subject<boolean>();

    constructor(
        public dialogRef: MatDialogRef<ReportingToolTemplateSelectionComponent>,
        private dialog: MatDialog,
        private templateService: ReportTemplateService,
        @Inject(MAT_DIALOG_DATA) public data: { selected: IReportTemplate; query: IQueryReportTemplate }
    ) {}

    get paginatedData(): IUser[] {
        return this.pagedData?.items ?? [];
    }

    ngOnInit(): void {
        disableBackgroundScrolling();
        this.subscribeToKeyUp();
        void this.loadData();
    }

    ngOnDestroy(): void {
        enableBackgroundScrolling();
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    protected changeSelected(element: IReportTemplate): void {
        this.pagedData.items.forEach(e => (e.selected = false));
        element.selected = !element.selected;
        this.value = this.pagedData.items.find(e => e.selected);
    }

    protected async loadData(event?: PageEvent): Promise<void> {
        if (
            !this.pagedData ||
            !event ||
            (this.pagedData &&
                event.pageIndex > this.pagedData.page &&
                (event.pageIndex + 1) * event.pageSize > this.pagedData.items.length)
        ) {
            const query: IQueryReportTemplate = {
                query: this.query.title,
                pagination: {
                    page: event?.pageIndex ?? 0,
                    pageSize: 20
                },
                ...this.data.query
            };

            const paginatedResult = await this.templateService.getAllItemsPaged(query);
            const data = paginatedResult.items;

            if (this.data?.selected) {
                data.forEach(e => ((e as IItemBase).selected = this.data?.selected.id === e.id));
            }

            if (event) {
                this.pagedData = {
                    ...paginatedResult,
                    items: [...(this.pagedData?.items ?? []), ...paginatedResult.items]
                };
            } else {
                this.pagedData = paginatedResult;
                setTimeout(() => this.setScrollListener(), 50);
            }
        }

        this.event = event ?? ({ pageIndex: 0, pageSize: 20 } as PageEvent);
    }

    protected closeDialog(): void {
        this.dialogRef.close({
            title: this.value.title,
            id: this.value.id,
            orgCode: this.value.orgCode,
            responsibleType: this.value.responsibleType
        });
    }

    protected async preview(template: IReportTemplate): Promise<void> {
        await firstValueFrom(
            this.dialog
                .open(TemplateEditorPreviewComponent, {
                    panelClass: 'custom-dialog',
                    width: `${(window.innerWidth * 2) / 3}px`,
                    data: template
                })
                .afterClosed()
        );
    }

    private subscribeToKeyUp(): void {
        this.keyUp
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(500),
                distinctUntilChanged(),
                filter(event => event.code === 'Enter'),
                tap(() => void this.loadData())
            )
            .subscribe();
    }

    private setScrollListener(): void {
        const itemContainer = this.itemContainer?.nativeElement;
        if (itemContainer) {
            itemContainer.onscroll = async () => {
                if (this.pagedData.hasMore) {
                    const pos = (itemContainer.scrollTop || itemContainer.scrollTop) + itemContainer.offsetHeight;
                    const max = itemContainer.scrollHeight;

                    if (pos >= max - 1 && !this.loading) {
                        this.loading = true;
                        this.event.pageIndex++;
                        await this.loadData(this.event);
                        this.loading = false;
                    }
                }
            };
        }
    }
}
